import React from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Layers, Plus, Trash2 } from "lucide-react";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import { WizardStepProps } from "@components/WizardCst/types";
import {
  buildRuleOperator,
  DynRule,
  DynRuleSet,
  DynamicGroupWizardData,
  parseRuleOperator,
} from "../types";
import { StepContainer } from "../../Wizard/components/StepLayout";
import { useRuleDataset } from "../useRuleDataset";
import PreviewCountBanner from "../PreviewCountBanner";

const EMPTY_RULE: DynRule = {
  operator: "AND",
  condition_field: "",
  condition: "",
  value: "",
};

const RulesStep: React.FC<WizardStepProps<DynamicGroupWizardData>> = ({
  data,
  setData,
}) => {
  const { t } = useTranslation("group-wizard-v2");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();
  const { fieldOptions, getConditionOptions } = useRuleDataset(corporateId);

  const includeOptions = [
    { label: t("rules.include"), value: "INCLUDE" },
    { label: t("rules.exclude"), value: "EXCLUDE" },
  ];
  const logicOptions = [
    { label: t("rules.all"), value: "AND" },
    { label: t("rules.any"), value: "OR" },
  ];

  const updateSets = (next: DynRuleSet[]) => setData({ sets: next });

  const patchRule = (si: number, ri: number, patch: Partial<DynRule>) => {
    const sets = data.sets.map((s, i) =>
      i !== si
        ? s
        : {
            ...s,
            rules: s.rules.map((r, j) => (j === ri ? { ...r, ...patch } : r)),
          },
    );
    updateSets(sets);
  };

  const setRuleOperator = (
    si: number,
    ri: number,
    logic: "AND" | "OR",
    exclude: boolean,
  ) => patchRule(si, ri, { operator: buildRuleOperator(logic, exclude) });

  const addCondition = (si: number) =>
    updateSets(
      data.sets.map((s, i) =>
        i === si ? { ...s, rules: [...s.rules, { ...EMPTY_RULE }] } : s,
      ),
    );

  const removeCondition = (si: number, ri: number) =>
    updateSets(
      data.sets.map((s, i) =>
        i === si ? { ...s, rules: s.rules.filter((_, j) => j !== ri) } : s,
      ),
    );

  const removeSet = (si: number) =>
    updateSets(data.sets.filter((_, i) => i !== si));

  const addSet = () =>
    updateSets([...data.sets, { operator: "AND", rules: [{ ...EMPTY_RULE }] }]);

  const setSetOperator = (si: number, operator: string) =>
    updateSets(data.sets.map((s, i) => (i === si ? { ...s, operator } : s)));

  const selectSx = {
    "& .MuiFormControl-root": {
      minWidth: "0 !important",
      width: "100% !important",
    },
    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
  };

  return (
    <StepContainer>
      {data.sets.map((set, si) => {
        const parsedSetLogic = set.operator?.startsWith("OR") ? "OR" : "AND";
        return (
          <React.Fragment key={si}>
            {si > 0 && (
              <Stack direction="row" alignItems="center" gap={2} sx={{ my: 1 }}>
                <Box
                  sx={{
                    flex: 1,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                />
                <Box sx={{ width: 200, ...selectSx }}>
                  <SelectCst
                    id={`set-op-${si}`}
                    label={t("rules.all")}
                    value={parsedSetLogic}
                    options={logicOptions}
                    onChange={(v: any) => setSetOperator(si, v || "AND")}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                />
              </Stack>
            )}

            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "12px",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                  {si + 1} {t("rules.ruleSet")}
                </Typography>
                {data.sets.length > 1 && (
                  <Button
                    onClick={() => removeSet(si)}
                    sx={{
                      textTransform: "none",
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    }}
                  >
                    {t("rules.deleteSet")}
                  </Button>
                )}
              </Box>

              {set.rules.map((rule, ri) => {
                const { logic, exclude } = parseRuleOperator(rule.operator);
                return (
                  <React.Fragment key={ri}>
                    {ri > 0 && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap={2}
                        sx={{ my: 0.5 }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            borderTop: `1px solid ${theme.palette.divider}`,
                          }}
                        />
                        <Box sx={{ width: 180, ...selectSx }}>
                          <SelectCst
                            id={`rule-logic-${si}-${ri}`}
                            label={t("rules.all")}
                            value={logic}
                            options={logicOptions}
                            onChange={(v: any) =>
                              setRuleOperator(si, ri, v || "AND", exclude)
                            }
                          />
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            borderTop: `1px solid ${theme.palette.divider}`,
                          }}
                        />
                      </Stack>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        flexWrap: "wrap",
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: "8px",
                        p: 1.5,
                      }}
                    >
                      <Box sx={{ width: 140, ...selectSx }}>
                        <SelectCst
                          id={`rule-incl-${si}-${ri}`}
                          label={t("rules.include")}
                          value={exclude ? "EXCLUDE" : "INCLUDE"}
                          options={includeOptions}
                          onChange={(v: any) =>
                            setRuleOperator(si, ri, logic, v === "EXCLUDE")
                          }
                        />
                      </Box>
                      <Typography sx={{ fontSize: "13px" }}>
                        {t("rules.usersWith")}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 160, ...selectSx }}>
                        <SelectCst
                          id={`rule-field-${si}-${ri}`}
                          label={t("rules.field")}
                          value={rule.condition_field}
                          options={fieldOptions}
                          onChange={(v: any) => {
                            const allowed = getConditionOptions(v).map(
                              (o) => o.value,
                            );
                            patchRule(si, ri, {
                              condition_field: v,
                              condition: allowed.includes(rule.condition)
                                ? rule.condition
                                : "",
                            });
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: "13px" }}>
                        {t("rules.that")}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 160, ...selectSx }}>
                        <SelectCst
                          id={`rule-cond-${si}-${ri}`}
                          label={t("rules.condition")}
                          value={rule.condition}
                          options={getConditionOptions(rule.condition_field)}
                          onChange={(v: any) =>
                            patchRule(si, ri, { condition: v })
                          }
                        />
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 160,
                          "& .MuiFormControl-root": {
                            minWidth: "0 !important",
                            width: "100% !important",
                          },
                          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                        }}
                      >
                        <InputCst
                          id={`rule-value-${si}-${ri}`}
                          label={t("rules.value")}
                          value={rule.value}
                          disabled={rule.condition === "NULL"}
                          onChange={(e: any) =>
                            patchRule(si, ri, { value: e.target.value })
                          }
                        />
                      </Box>
                      <IconButton
                        onClick={() => removeCondition(si, ri)}
                        sx={{ color: theme.palette.text.tertiary }}
                        aria-label={t("rules.deleteCondition") || "delete"}
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    </Box>
                  </React.Fragment>
                );
              })}

              <Box>
                <Button
                  variant="contained"
                  startIcon={<Plus size={16} />}
                  onClick={() => addCondition(si)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 700,
                  }}
                >
                  {t("rules.addCondition")}
                </Button>
              </Box>
            </Box>
          </React.Fragment>
        );
      })}

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button
          variant="outlined"
          startIcon={<Layers size={16} />}
          onClick={addSet}
          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
        >
          {t("rules.addSet")}
        </Button>
      </Box>

      <PreviewCountBanner corporateId={corporateId} sets={data.sets} />
    </StepContainer>
  );
};

export default RulesStep;
